import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Plus, ChevronDown, ChevronUp, History } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useCompany } from '../../../../context/CompanyContext';
import EnrollmentCard from './EnrollmentCard';
import EnrollmentSheet from './EnrollmentSheet';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ProgramsSection — Phase 2 Multi-Program presentation for the Client
 * Profile.
 *
 * Fetches all enrollments (active + historical) for the client from the
 * backend and renders:
 *   • One <EnrollmentCard> per ACTIVE enrollment
 *   • A collapsible History section listing ended/superseded ones
 *   • An always-available "+ Add Program" button that opens
 *     <EnrollmentSheet mode="add">
 *
 * The section auto-lazy-migrates a legacy `lead.recurrence` singleton on
 * first GET so existing single-program clients see the same
 * information they always did — just rendered as one first-class card.
 *
 * Empty state (no enrollments and no legacy recurrence): renders the
 * "Set up a program" empty card, unchanged from V1.
 */
const ProgramsSection = ({ client }) => {
  const { companyId } = useCompany();
  const [programs, setPrograms] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Load company-wide Service Program templates ─────────────────────
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken();
        const res = await axios.get(`${API}/api/service-programs/`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        setPrograms(res.data || []);
      } catch (err) {
        console.warn('[ProgramsSection] Could not load programs:', err);
      }
    })();
  }, [companyId]);

  // ── Load enrollments (lazy-migrates legacy) ─────────────────────────
  const loadEnrollments = useCallback(async () => {
    if (!companyId || !client?.id) return;
    setLoading(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(
        `${API}/api/leads/${client.id}/enrollments`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setEnrollments(res.data?.enrollments || []);
    } catch (err) {
      console.warn('[ProgramsSection] Could not load enrollments:', err);
      setEnrollments([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, client?.id]);

  useEffect(() => { loadEnrollments(); }, [loadEnrollments]);

  // ── Real-time enrollment progress (freshness) ───────────────────────
  // The REST load above triggers the lazy-migration side effect and gives
  // the canonical initial (ordered) list. This tenant-scoped listener keeps
  // the AUTHORITATIVE enrollment progress fresh — so completing a visit
  // updates the mounted EnrollmentCard's "N of M completed" counter without
  // a full browser refresh. It merges the live doc's mutable fields onto the
  // REST list (preserving ordering/active behavior) and appends any live
  // enrollment the REST list doesn't yet know about.
  //
  // Strictly scoped to companies/{companyId}/leads/{leadId}/enrollments — no
  // global or collectionGroup query. Unsubscribes on unmount / lead change.
  useEffect(() => {
    if (!companyId || !client?.id) return undefined;
    const col = collection(db, 'companies', companyId, 'leads', client.id, 'enrollments');
    const unsub = onSnapshot(
      col,
      (snap) => {
        const live = {};
        snap.forEach((d) => { live[d.id] = d.data() || {}; });
        setEnrollments((prev) => {
          const merged = prev.map((e) => {
            const ld = live[e.enrollmentId];
            if (!ld) return e;
            return {
              ...e,
              completedOccurrences: ld.completedOccurrences ?? e.completedOccurrences,
              totalOccurrences:     ld.totalOccurrences ?? e.totalOccurrences,
              status:               ld.status ?? e.status,
              nextServiceDate:      ld.nextServiceDate ?? e.nextServiceDate,
              lastServiceDate:      ld.lastServiceDate ?? e.lastServiceDate,
            };
          });
          const known = new Set(prev.map((e) => e.enrollmentId));
          snap.forEach((d) => {
            if (!known.has(d.id)) merged.push({ enrollmentId: d.id, ...(d.data() || {}) });
          });
          return merged;
        });
      },
      (err) => console.warn('[ProgramsSection] enrollment listener error:', err),
    );
    return () => unsub();
  }, [companyId, client?.id]);

  const activeEnrollments = enrollments.filter((e) => e.status === 'active');
  const historyEnrollments = enrollments.filter((e) => e.status !== 'active');
  const hasAnyEnrollment = enrollments.length > 0;

  return (
    <section
      className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4"
      data-testid="programs-section"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-900">Programs</h2>
          {activeEnrollments.length > 0 && (
            <span
              className="text-[11px] font-medium bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5"
              data-testid="programs-active-count"
            >
              {activeEnrollments.length} active
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors"
          data-testid="programs-add-btn"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Program
        </button>
      </header>

      {/* ── Active enrollments (or empty state) ────────────────────── */}
      <div className="space-y-4" data-testid="programs-list">
        {addOpen && (
          <EnrollmentSheet
            mode="add"
            client={client}
            programs={programs}
            onClose={() => setAddOpen(false)}
            onSaved={() => { setAddOpen(false); loadEnrollments(); }}
          />
        )}

        {loading && !hasAnyEnrollment && (
          <div className="text-xs text-gray-400 italic" data-testid="programs-loading">
            Loading programs…
          </div>
        )}

        {!loading && activeEnrollments.length === 0 && !addOpen && (
          <EmptyProgramsCard onSetup={() => setAddOpen(true)} />
        )}

        {activeEnrollments.map((enr) => (
          <EnrollmentCard
            key={enr.enrollmentId}
            enrollment={enr}
            client={client}
            programs={programs}
            onMutated={loadEnrollments}
          />
        ))}
      </div>

      {/* ── History (collapsed by default) ────────────────────────── */}
      {historyEnrollments.length > 0 && (
        <div data-testid="programs-history-section">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
            data-testid="programs-history-toggle"
            aria-expanded={historyOpen}
          >
            <span className="text-xs font-semibold text-gray-700 inline-flex items-center gap-1.5">
              <History className="h-3.5 w-3.5 text-gray-500" />
              History — {historyEnrollments.length} past program{historyEnrollments.length === 1 ? '' : 's'}
            </span>
            {historyOpen
              ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
              : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-3" data-testid="programs-history-body">
              {historyEnrollments.map((enr) => (
                <EnrollmentCard
                  key={enr.enrollmentId}
                  enrollment={enr}
                  client={client}
                  programs={programs}
                  onMutated={loadEnrollments}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ProgramsSection;

// ─── Empty-state card ─────────────────────────────────────────────────
function EmptyProgramsCard({ onSetup }) {
  return (
    <div
      className="bg-white rounded-md border border-dashed border-gray-300 p-4"
      data-testid="program-card-empty"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-gray-300" />
          <h3 className="text-sm font-semibold text-gray-500">Program</h3>
          <span
            className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full px-2.5 py-0.5"
            data-testid="program-empty-pill"
          >
            None yet
          </span>
        </div>
      </div>
      <p className="text-xs text-gray-500 leading-snug mb-3">
        Add a recurring program to schedule visits automatically and track history in one place.
      </p>
      <button
        type="button"
        onClick={onSetup}
        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
        data-testid="program-empty-setup-btn"
      >
        <Plus className="h-3.5 w-3.5" />
        Add Program
      </button>
    </div>
  );
}
